import axios, { AxiosRequestConfig } from "axios";
import { ReqMethods, ResponseStatus } from "../enums";
import env from "../config";
import { useMarketplaceOptional } from "../marketplace/context";
import { getAppToken } from "../marketplace/auth";

const BASE_URL = `${env.API_URL}`;

type ServiceOptions = {
  query?: string;
  payload?: any;
  headers?: Record<string, string>;
};

type HttpMethod = "get" | "post" | "put" | "delete";

// Unwraps the standard { success, message, data } envelope.
function useServiceHandler() {
  return (
      fn: (
        method: HttpMethod,
        path: string,
        options?: ServiceOptions,
      ) => Promise<any>,
    ) =>
    async (method: HttpMethod, path: string, options: ServiceOptions = {}) => {
      try {
        const res = await fn(method, path, options);
        const body = res.data;
        const response =
          body && typeof body === "object" && "success" in body && "data" in body
            ? body.data
            : body;
        return { response, meta: body };
      } catch (err: any) {
        if (
          err.response &&
          err.response.status === ResponseStatus.UNAUTHORIZED
        ) {
          return { error: err.response?.data || err };
        }
        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const serviceHandler = useServiceHandler();

  const CallService = (
    method: HttpMethod,
    path: string,
    { query, payload, headers = {} }: ServiceOptions = {},
  ) => {
    const url = query ? `${path}?${query}` : path;
    const config: AxiosRequestConfig = { headers };
    if (method === "get" || method === "delete") return axios[method](url, config);
    return axios[method](url, payload, config);
  };

  return serviceHandler(CallService);
}

// Every authenticated request goes through this hook. It attaches the signed
// session token (Authorization: Bearer) — the backend's source of truth — and,
// during the transition, the legacy `x-account-id` header as a fallback.
function useAppServices() {
  const { GET, POST, PUT, DELETE }: any = ReqMethods;
  const CallService = useCallService();
  // May render outside MarketplaceProvider (e.g. /integration OAuth callback),
  // where there's no account yet — fall back to no x-account-id header.
  const accountId = useMarketplaceOptional()?.accountId ?? "";

  const wrap = (method: HttpMethod, path: string, options: ServiceOptions = {}) => {
    const token = getAppToken();
    return CallService(method, path, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(accountId ? { "x-account-id": accountId } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  };

  return {
    url_tracker: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker`, options),
      filter: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker/filter`, options),
      slugAvailable: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker/slug_available`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/url_tracker`, options),
      bulkCreate: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/url_tracker/bulk`, options),
      update: (options: ServiceOptions) =>
        wrap(PUT, `${BASE_URL}/url_tracker`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/url_tracker`, options),
    },
    url_tracker_logs: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker_logs`, options),
      history: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker_logs/history`, options),
      // Server-side rolled-up analytics (account-wide for the dashboard).
      summary: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker_logs/summary`, options),
      // Server-side rolled-up analytics for a single link.
      summaryByTracker: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker_logs/summary_by_tracker`, options),
      // Paginated raw click rows for the detail page table.
      page: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker_logs/page`, options),
      // Per-link daily click counts for the links-list sparklines.
      sparklines: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_tracker_logs/sparklines`, options),
    },
    url_domains: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/url_domains`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/url_domains`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/url_domains`, options),
      verify: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/url_domains/verify`, options),
      setDefault: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/url_domains/default`, options),
    },
    campaigns: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/campaigns`, options),
      filter: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/campaigns/filter`, options),
      stats: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/campaigns/stats`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/campaigns`, options),
      update: (options: ServiceOptions) =>
        wrap(PUT, `${BASE_URL}/campaigns`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/campaigns`, options),
    },
    landing_pages: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/landing_pages`, options),
      filter: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/landing_pages/filter`, options),
      slugAvailable: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/landing_pages/slug_available`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/landing_pages`, options),
      update: (options: ServiceOptions) =>
        wrap(PUT, `${BASE_URL}/landing_pages`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/landing_pages`, options),
    },
     auth: {
      integrate_app: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/auth/integrate_app`, options),
      agency_install: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/auth/agency_install`, options),
    },
     services: {
      ghl: {
        call_service: (options: ServiceOptions) =>
          wrap(POST, `${BASE_URL}/services/ghl/`, options),
      },
    },
    account: {
      updateBranding: (options: ServiceOptions) =>
        wrap(PUT, `${BASE_URL}/accounts/branding`, options),
    },
    utm_presets: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/utm_presets`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/utm_presets`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/utm_presets`, options),
    },
    qr_templates: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/qr_templates`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/qr_templates`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/qr_templates`, options),
    },
    link_tags: {
      get: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/link_tags`, options),
      create: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/link_tags`, options),
      update: (options: ServiceOptions) =>
        wrap(PUT, `${BASE_URL}/link_tags`, options),
      delete: (options: ServiceOptions) =>
        wrap(DELETE, `${BASE_URL}/link_tags`, options),
    },
    // Account-scoped GHL data (uses the token saved on the Account; the
    // x-account-id header is attached automatically by `wrap`).
    ghl: {
      tags: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/ghl/tags`, options),
      custom_fields: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/ghl/custom_fields`, options),
      create_custom_field: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/ghl/custom_fields`, options),
      contact: (options: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/ghl/contact`, options),
      funnels: (options?: ServiceOptions) =>
        wrap(GET, `${BASE_URL}/ghl/funnels`, options),
      upload_image: (options: ServiceOptions) =>
        wrap(POST, `${BASE_URL}/ghl/upload-image`, options),
    },
  };
}

export { useAppServices, useCallService };
